import React, { useState, useEffect } from 'react';

// --- Cache Purge & Force Refresh Helper ---
export async function forceCacheRefreshAndReload(newVersion?: string) {
  console.log('[Cache Manager] Executing automatic force cache refresh & reload...');
  try {
    // 1. Purge all Service Worker cache storage buckets
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // 2. Clear app version markers or record new version
    if (newVersion) {
      localStorage.setItem('rovita_app_version', newVersion);
      sessionStorage.setItem('rovita_reloaded_for_version', newVersion);
    } else {
      localStorage.removeItem('rovita_app_version');
    }

    // 3. Message active service workers to clear internal caches & skip waiting
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        try {
          await reg.update();
          if (reg.active) {
            reg.active.postMessage({ type: 'CLEAR_ALL_CACHES' });
            reg.active.postMessage({ type: 'SKIP_WAITING' });
          }
        } catch (e) {}
      }
    }
  } catch (err) {
    console.warn('[Cache Manager] Refresh warning:', err);
  } finally {
    // Append timestamp query parameter to bypass browser disk cache on reload
    const url = new URL(window.location.href);
    url.searchParams.set('_t', Date.now().toString());
    window.location.replace(url.toString());
  }
}

// Expose force refresh on window for manual/global access
if (typeof window !== 'undefined') {
  (window as any).forceAppCacheRefresh = forceCacheRefreshAndReload;
}

// --- Singleton PWA & ServiceWorker Manager ---
let globalDeferredPrompt: any = null;
const promptListeners = new Set<(prompt: any) => void>();
let isSwRegistered = false;
let isVersionManagerStarted = false;

function setGlobalPrompt(prompt: any) {
  globalDeferredPrompt = prompt;
  promptListeners.forEach(listener => listener(prompt));
}

async function checkAppVersionOnce() {
  try {
    const res = await fetch('/api/version?t=' + Date.now(), {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
    if (!res.ok) return;
    const data = await res.json();
    const serverVersion = data.version;
    const currentVersion = localStorage.getItem('rovita_app_version');
    const reloadedVersion = sessionStorage.getItem('rovita_reloaded_for_version');

    if (!currentVersion) {
      localStorage.setItem('rovita_app_version', serverVersion);
    } else if (currentVersion !== serverVersion && reloadedVersion !== serverVersion) {
      console.log(`[Cache Manager] Server version updated (${currentVersion} -> ${serverVersion}). Auto-refreshing cache...`);
      await forceCacheRefreshAndReload(serverVersion);
    }
  } catch (err) {
    console.debug('[Cache Manager] Version check error:', err);
  }
}

function initGlobalPwaListeners() {
  if (typeof window === 'undefined') return;

  if (!isSwRegistered && 'serviceWorker' in navigator) {
    isSwRegistered = true;
    const registerSW = () => {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        // Register once cleanly
        reg.update().catch(() => {});
        reg.onupdatefound = () => {
          const installingWorker = reg.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[Cache Manager] New Service Worker version detected. Auto-refreshing client...');
                checkAppVersionOnce();
              }
            };
          }
        };
      }).catch((err) => {
        console.debug('ServiceWorker registration notice:', err);
      });
    };

    if (document.readyState === 'complete') {
      registerSW();
    } else {
      window.addEventListener('load', registerSW, { once: true });
    }
  }

  if (!isVersionManagerStarted) {
    isVersionManagerStarted = true;
    checkAppVersionOnce();
    setInterval(checkAppVersionOnce, 30000);

    const handleFocusOrVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkAppVersionOnce();
      }
    };
    window.addEventListener('focus', handleFocusOrVisibility);
    window.addEventListener('online', handleFocusOrVisibility);
    document.addEventListener('visibilitychange', handleFocusOrVisibility);

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      setGlobalPrompt(e);
    });

    window.addEventListener('appinstalled', () => {
      setGlobalPrompt(null);
    });
  }
}

// Start listeners once on module load in browser
if (typeof window !== 'undefined') {
  initGlobalPwaListeners();
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(globalDeferredPrompt);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    // 1. Subscribe to shared prompt updates
    const handlePromptChange = (prompt: any) => {
      setDeferredPrompt(prompt);
    };
    promptListeners.add(handlePromptChange);
    setDeferredPrompt(globalDeferredPrompt);

    // 2. Detect standalone display mode
    const checkStandalone = () => {
      const standalone = 
        window.matchMedia('(display-mode: standalone)').matches || 
        (navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      setIsStandalone(standalone);
      return standalone;
    };
    checkStandalone();

    // 3. Detect iOS
    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    return () => {
      promptListeners.delete(handlePromptChange);
    };
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => {
    setShowModal(false);
    sessionStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  const triggerInstall = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        if (choiceResult && choiceResult.outcome === 'accepted') {
          setDeferredPrompt(null);
          setShowModal(false);
        }
      } catch (err) {
        console.error('Error invoking native PWA prompt:', err);
      }
    }
  };

  return {
    isStandalone,
    isIOS,
    hasNativePrompt: !!deferredPrompt,
    showModal,
    openModal,
    closeModal,
    triggerInstall
  };
}

export const InstallPwaModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onNativeInstall?: () => void;
  hasNativePrompt?: boolean;
}> = ({ isOpen, onClose, onNativeInstall, hasNativePrompt }) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
  const isIOS = /iphone|ipad|ipod/.test(ua) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIOSChrome = isIOS && /crios|crmo/.test(ua);
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768 && !isIOS && !/android|mobile/.test(ua);

  const handleInstallClick = async () => {
    if (hasNativePrompt && onNativeInstall) {
      try {
        await onNativeInstall();
      } catch (e) {
        setShowInstructions(true);
      }
    } else {
      // If native browser prompt is not active, immediately show instructions
      setShowInstructions(true);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    /* Floating corner popup - NO full screen backdrop overlay! */
    <div 
      role="dialog"
      aria-label="Add ROVITA App to Home Screen"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[99999] w-[calc(100%-2rem)] sm:w-96 bg-white/95 backdrop-blur-md rounded-2xl p-4 md:p-5 shadow-2xl border-2 border-indigo-100/90 text-left space-y-3.5 animate-in slide-in-from-bottom-5 duration-300 pointer-events-auto"
      style={{ isolation: 'isolate' }}
    >
      {/* Decorative top accent line */}
      <div className="absolute top-0 left-6 right-6 h-1 bg-gradient-to-r from-teal-500 via-indigo-600 to-indigo-500 rounded-b-full" aria-hidden="true"></div>

      {/* Header with ROVITA logo and Close button */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center p-1.5 shadow-sm border border-slate-200/80 shrink-0">
            <img src="/logo.png" alt="ROVITA App Icon" className="w-full h-full object-contain" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-900 tracking-tight leading-tight">
              Add ROVITA App
            </h3>
            <p className="text-[10px] font-bold text-teal-700 uppercase tracking-wider">
              {isDesktop ? 'Desktop App' : 'Mobile App'}
            </p>
          </div>
        </div>

        <button 
          onClick={onClose}
          className="w-7 h-7 bg-slate-100 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-full flex items-center justify-center transition-all shrink-0"
          aria-label="Close Install Prompt"
          title="Dismiss"
        >
          <i className="fa-solid fa-xmark text-xs" aria-hidden="true"></i>
        </button>
      </div>

      <p className="text-xs text-slate-600 font-medium leading-relaxed">
        Install <strong className="text-slate-900">ROVITA Telemedicine</strong> on your {isDesktop ? 'Desktop' : 'Home screen'} for 1-tap instant access.
      </p>

      {/* Primary Actions */}
      <div className="space-y-2">
        <button
          onClick={handleInstallClick}
          className="w-full py-2.5 px-3.5 bg-gradient-to-r from-teal-700 to-indigo-600 hover:from-teal-800 hover:to-indigo-700 text-white font-black rounded-xl uppercase tracking-wider text-[11px] shadow-md shadow-teal-900/10 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <i className="fa-solid fa-download text-xs"></i>
          {hasNativePrompt ? 'Add to Home Screen' : 'Install App'}
        </button>

        <div className="flex items-center justify-between px-1">
          <button
            onClick={() => setShowInstructions(prev => !prev)}
            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 underline decoration-indigo-200 underline-offset-2 transition-colors"
          >
            {showInstructions ? 'Hide Instructions' : 'How to install manually?'}
          </button>
          <button
            onClick={onClose}
            className="text-[11px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
          >
            Not Now
          </button>
        </div>
      </div>

      {/* Step-by-Step Instructions Panel (Auto-shown or toggled) */}
      {showInstructions && (
        <div className="pt-3 border-t border-slate-100 text-xs text-slate-700 space-y-2.5 bg-slate-50/80 p-3 rounded-xl border border-slate-200/60 animate-in fade-in duration-200">
          {isIOSChrome ? (
            <>
              <p className="font-bold text-slate-900 flex items-center gap-1.5 text-[11px]">
                <i className="fa-brands fa-chrome text-indigo-600"></i> iPhone Chrome Installation:
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-[11px] font-medium text-slate-600">
                <li>
                  Tap <strong className="text-indigo-700">Share</strong> <i className="fa-solid fa-arrow-up-from-bracket text-indigo-500"></i> in top right corner of Chrome.
                </li>
                <li>
                  Scroll down & tap <strong className="text-indigo-700">Add to Home Screen</strong> <i className="fa-solid fa-square-plus text-indigo-500"></i>.
                </li>
                <li>
                  <em>If option is missing:</em> Tap <strong className="text-indigo-700">Open in Safari</strong> <i className="fa-solid fa-compass text-indigo-500"></i> then tap Share &rarr; Add to Home Screen.
                </li>
              </ol>
              <div className="pt-1">
                <button
                  onClick={handleCopyLink}
                  className="w-full py-1.5 px-2 bg-white hover:bg-slate-100 text-slate-700 font-bold rounded-lg border border-slate-300 text-[10px] flex items-center justify-center gap-1.5 transition-all"
                >
                  <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-link text-indigo-600'}`}></i>
                  {copied ? 'Link Copied! Paste in Safari' : 'Copy Link to Open in Safari'}
                </button>
              </div>
            </>
          ) : isIOS ? (
            <>
              <p className="font-bold text-slate-900 flex items-center gap-1.5 text-[11px]">
                <i className="fa-brands fa-apple text-slate-800"></i> iOS Safari Installation:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-[11px] font-medium text-slate-600">
                <li>Tap <strong className="text-indigo-700">Share</strong> <i className="fa-solid fa-arrow-up-from-bracket text-indigo-500"></i> in Safari toolbar.</li>
                <li>Scroll down & tap <strong className="text-indigo-700">Add to Home Screen</strong> <i className="fa-solid fa-square-plus text-indigo-500"></i>.</li>
              </ol>
            </>
          ) : isDesktop ? (
            <>
              <p className="font-bold text-slate-900 flex items-center gap-1.5 text-[11px]">
                <i className="fa-solid fa-desktop text-indigo-600"></i> Desktop Installation (Chrome / Edge):
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-[11px] font-medium text-slate-600">
                <li>
                  Look for the <strong className="text-indigo-700">Install icon</strong> (<i className="fa-solid fa-circle-down text-indigo-600 mx-0.5"></i> or <i className="fa-solid fa-desktop text-indigo-600 mx-0.5"></i>) on the right side of your browser’s address bar at the top.
                </li>
                <li>
                  Or click browser menu (<strong className="text-slate-800">⋮</strong> or <strong className="text-slate-800">...</strong>) &rarr; <strong className="text-indigo-700">Save and Share</strong> &rarr; <strong className="text-indigo-700">Install ROVITA App</strong>.
                </li>
              </ol>
              <div className="pt-1">
                <button
                  onClick={handleCopyLink}
                  className="w-full py-1.5 px-2 bg-white hover:bg-slate-100 text-slate-700 font-bold rounded-lg border border-slate-300 text-[10px] flex items-center justify-center gap-1.5 transition-all"
                >
                  <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-link text-indigo-600'}`}></i>
                  {copied ? 'Direct App Link Copied!' : 'Copy Direct App Link'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="font-bold text-slate-900 flex items-center gap-1.5 text-[11px]">
                <i className="fa-solid fa-mobile-screen-button text-indigo-600"></i> Mobile Android Installation:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-[11px] font-medium text-slate-600">
                <li>Tap browser menu (<strong className="text-slate-800">⋮</strong> in top right).</li>
                <li>Select <strong className="text-indigo-700">Add to Home screen</strong> or <strong className="text-indigo-700">Install App</strong>.</li>
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
};


